import React, { useState, useCallback } from 'react';
import {
  makeStyles,
  mergeClasses,
  Text,
  tokens,
} from '@fluentui/react-components';
import {
  ChevronDown20Regular,
  Folder20Regular,
} from '@fluentui/react-icons';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, pointerWithin, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { motion, AnimatePresence } from 'framer-motion';
import { AppHeader } from '../../components/app-header';
import { FolderSidebar } from '../../components/folder-sidebar';
import { RecordingsList } from '../../components/recordings-list';
import { RecordingDrawer } from '../../components/recording-drawer';
import { FolderAssignDialog } from '../../components/folder-assign-dialog';
import { DragOverlayCard } from '../../components/drag-overlay-card';
import { useIsLargeScreen } from '../../utils/use-is-large-screen';
import { openRecordingInTeamsWindow } from '../../utils/open-in-teams-window';
import { useFoldersQuery, useAssignRecordingToFolderMutation, useRemoveRecordingFromFolderMutation } from '../../queries/folders';

// Hauteur du header de l'application (doit correspondre à AppHeader)
const HEADER_HEIGHT = '68px';

const useStyles = makeStyles({
  page: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground3,
  },
  content: {
    flex: 1,
    display: 'flex',
    minHeight: 0,
    overflow: 'hidden',
  },
  sidebar: {
    width: '260px',
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  sidebarCollapsed: {
    width: '0px',
    overflow: 'hidden',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
  },
  mobileLayout: {
    flexDirection: 'column',
  },
  mobileContentWrapper: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    position: 'relative' as const,
    overflow: 'hidden',
  },
  mobileFolderTogglePlaceholder: {
    minWidth: '36px',
    flexShrink: 0,
  },
  mobileFolderToggle: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    height: `100vh`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXS}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    writingMode: 'sideways-lr' as const,
    textOrientation: 'mixed' as const,
    minWidth: '36px',
    zIndex: 10,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  mobileFolderToggleOpen: {
    backgroundColor: tokens.colorBrandBackground2,
    borderRight: `1px solid ${tokens.colorBrandStroke1}`,
  },
  mobileFolderToggleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
  },
  mobileFolderToggleIcon: {
    color: tokens.colorBrandForeground1,
    transform: 'rotate(-90deg)',
  },
  mobileFolderToggleText: {
    fontWeight: tokens.fontWeightMedium,
    fontSize: tokens.fontSizeBase200,
  },
  mobileFolderToggleChevron: {
    color: tokens.colorNeutralForeground3,
    transitionProperty: 'transform',
    transitionDuration: '0.2s',
    transform: 'rotate(-90deg)',
  },
  mobileFolderToggleChevronOpen: {
    transform: 'rotate(90deg)',
  },
  mobileSidebarWrapper: {
    position: 'fixed' as const,
    top: 0,
    left: '36px',
    height: `100vh`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    zIndex: 10,
    overflow: 'hidden',
  },
  mobileSidebar: {
    width: 'auto',
    minWidth: '240px',
    height: '100%',
    borderRight: 'none',
    overflowY: 'auto' as const,
  },
});

export const HomePage = () => {
  const styles = useStyles();
  const isLargeScreen = useIsLargeScreen();
  const { data: foldersData } = useFoldersQuery();
  const folders = foldersData?.folders;
  const assignMutation = useAssignRecordingToFolderMutation();
  const removeMutation = useRemoveRecordingFromFolderMutation();

  // Configure drag sensor with activation constraint to allow clicks
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum 8px movement before drag starts
      },
    })
  );

  const [displayedRecordingId, setDisplayedRecordingId] = useState<string | undefined>(undefined);
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(undefined);
  const [assigningRecording, setAssigningRecording] = useState<{
    recordingId: string;
    currentFolderId?: string;
  } | null>(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [draggingRecording, setDraggingRecording] = useState<{ id: string; name: string } | null>(null);

  // Get current folder name for mobile toggle
  const currentFolderName = selectedFolderId === 'unclassified'
    ? 'Non classées'
    : selectedFolderId
      ? folders?.find(f => f.id === selectedFolderId)?.name || 'Dossier'
      : 'Toutes les réunions';

  const handleOpenDrawer = useCallback((id: string) => {
    setDisplayedRecordingId(id);
  }, []);

  const handleOpenNewWindow = useCallback((id: string) => {
    openRecordingInTeamsWindow(id);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDisplayedRecordingId(undefined);
  }, []);

  const handleAssignFolder = useCallback((recordingId: string, currentFolderId?: string) => {
    setAssigningRecording({ recordingId, currentFolderId });
  }, []);

  const handleCloseAssignDialog = useCallback(() => {
    setAssigningRecording(null);
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current) {
      setDraggingRecording({
        id: active.id as string,
        name: active.data.current.name as string,
      });
    }
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setDraggingRecording(null);

    if (!over) return;

    const recordingId = active.id as string;
    const targetFolderId = over.id as string;
    const currentFolderId = active.data.current?.folderId as string | undefined;

    // Don't do anything if dropped on the same folder
    if (targetFolderId === currentFolderId) return;

    // Handle "unclassified" drop - remove from current folder
    if (targetFolderId === 'unclassified' && currentFolderId) {
      removeMutation.mutate({ folderId: currentFolderId, recordingId });
    }
    // Handle folder drop - assign to new folder
    else if (targetFolderId !== 'all' && targetFolderId !== 'unclassified') {
      assignMutation.mutate({ folderId: targetFolderId, recordingId });
    }
  }, [assignMutation, removeMutation]);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      collisionDetection={pointerWithin}
    >
      <div className={styles.page}>
        <AppHeader />

        <div className={styles.content}>
          {isLargeScreen ? (
            <>
              <div className={styles.sidebar}>
                <FolderSidebar
                  selectedFolderId={selectedFolderId}
                  onFolderSelect={setSelectedFolderId}
                  isDragging={!!draggingRecording}
                />
              </div>
              <main className={styles.main}>
                <RecordingsList
                  folderId={selectedFolderId}
                  activeRecordingId={displayedRecordingId}
                  onOpenDrawer={handleOpenDrawer}
                  onOpenNewWindow={handleOpenNewWindow}
                  onAssignFolder={handleAssignFolder}
                />
              </main>
            </>
          ) : (
          <div className={styles.mobileContentWrapper}>
            {/* Placeholder to reserve space for the fixed toggle */}
            <div className={styles.mobileFolderTogglePlaceholder} />

            {/* Mobile folder toggle - fixed position */}
            <div
              className={mergeClasses(
                styles.mobileFolderToggle,
                isMobileSidebarOpen && styles.mobileFolderToggleOpen
              )}
              onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            >
              <ChevronDown20Regular
                className={mergeClasses(
                  styles.mobileFolderToggleChevron,
                  isMobileSidebarOpen && styles.mobileFolderToggleChevronOpen
                )}
              />
              <div className={styles.mobileFolderToggleContent}>
                <Folder20Regular className={styles.mobileFolderToggleIcon} />
                <Text className={styles.mobileFolderToggleText}>
                  {currentFolderName}
                </Text>
              </div>
            </div>

            {/* Collapsible folder panel - horizontal */}
            <AnimatePresence>
              {isMobileSidebarOpen && (
                <motion.div
                  className={styles.mobileSidebarWrapper}
                  initial={{ width: 0 }}
                  animate={{ width: 'auto' }}
                  exit={{ width: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <div className={styles.mobileSidebar}>
                    <FolderSidebar
                      selectedFolderId={selectedFolderId}
                      onFolderSelect={(folderId) => {
                        setSelectedFolderId(folderId);
                        setIsMobileSidebarOpen(false);
                      }}
                      isDragging={!!draggingRecording}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <main className={styles.main}>
              <RecordingsList
                folderId={selectedFolderId}
                activeRecordingId={displayedRecordingId}
                onOpenDrawer={handleOpenDrawer}
                onOpenNewWindow={handleOpenNewWindow}
                onAssignFolder={handleAssignFolder}
              />
            </main>
          </div>
        )}
      </div>

      {displayedRecordingId && (
        <RecordingDrawer
          recordingId={displayedRecordingId}
          onClose={handleCloseDrawer}
        />
      )}

      <FolderAssignDialog
        recordingId={assigningRecording?.recordingId ?? null}
        currentFolderId={assigningRecording?.currentFolderId}
        onClose={handleCloseAssignDialog}
      />

      <DragOverlay modifiers={[snapCenterToCursor]}>
        {draggingRecording && (
          <DragOverlayCard name={draggingRecording.name} />
        )}
      </DragOverlay>
    </div>
    </DndContext>
  );
};
